import { TimeFrame } from '@hud/portal-dtos/webapp-common.dto';

import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useGlobalFilter } from './useGlobalFilter';
import { parseTimeFrame, stringifyTimeFrame } from '../utils';

// Define the shape of the context data for TypeScript (optional)
interface TimeFrameContextType {
  timeFrame: TimeFrame;
  setTimeFrame: (timeFrame: TimeFrame) => void;
}

// Create a context with a default value (will be overridden by the provider)
const TimeFrameContext = createContext<TimeFrameContextType | undefined>(
  undefined,
);

// Provider component
export const TimeFrameProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [rawTimeFrame, setRawTimeFrame] = useGlobalFilter('timeFrame', 'week');

  const handleSetTimeFrame = useCallback(
    (timeFrame: TimeFrame) => {
      setRawTimeFrame(stringifyTimeFrame(timeFrame));
    },
    [setRawTimeFrame],
  );

  const timeFrame = parseTimeFrame(rawTimeFrame);

  const timeFrameContextValue = useMemo(() => {
    return {
      timeFrame,
      setTimeFrame: handleSetTimeFrame,
    };
  }, [timeFrame, setRawTimeFrame]);

  return (
    <TimeFrameContext.Provider value={timeFrameContextValue}>
      {children}
    </TimeFrameContext.Provider>
  );
};

export default function useTimeFrame(): TimeFrameContextType {
  const context = useContext(TimeFrameContext);
  if (context === undefined) {
    throw new Error('useTimeFrame must be used within a TimeFrameProvider');
  }
  return context;
}
